import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '../components/ui/dialog';

import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Plus,
  Search,
  Mail,
  Phone,
  Calendar,
  Key,
  UserX,
  Shield,
  Trash2,
  UserCheck,
  MoreHorizontal,
  Filter,
  X,
  Eye,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Folder,
  FileText,
  SearchIcon,
  Check,
  UserPlus,
  Send,
  Building2,
  Users
} from 'lucide-react';
import { useState, useMemo, useEffect } from 'react';
import { formatMemberSinceDate, getRoleColor, getStatusColor, isSubscriptionValid } from '@/helpers/common';
import { WorkgroupRequestsAPI, WorkgroupRequest, WorkgroupSearchResult } from '../api/workgroupRequests';
import { useTeams } from '../hooks/useTeams';
import { useAuth } from '../contexts/AuthContext';
import { useWorkgroups } from '../hooks/useWorkgroups';
import { TEAM_ROLES, TEAM_DEPARTMENTS } from '../helpers/collections';
import { DashboardAPI, DashboardMetrics } from '../api/dashboard';
import { Card, CardContent } from '@/components/ui/card';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { ActivateDeactivateTeamMember } from '../components/ActivateTeamMember';
import { DeleteTeamMember } from '@/components/DeleteTeamMember';
import { ResetMemberPassword } from '../components/ResetMemberPassword';
import { EditPermissions } from '../components/EditPermissions';
import { TeamMemberDetails } from '../components/TeamMemberDetails';
import { ChangeMemberRole } from '../components/ChangeMemberRole';
import { RolesManagement } from './RolesManagement';
import { useRoles } from '../hooks/useRoles';
import { useHasPermission } from '../hooks/useHasPermission';
import { usePackageLimits } from '../hooks/usePackageLimits';
import { GatedControl } from '../components/GatedControl';
import { Billing } from './Billing';

interface TeamProps {
  workgroupId: number;
}

export function Team({ workgroupId }: TeamProps) {
  const { user, subscription } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState<string>('all');
  const [designationFilter, setDesignationFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('firstname');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedMemberForDelete, setSelectedMemberForDelete] = useState<any>(null);
  const [selectedMemberForView, setSelectedMemberForView] = useState<any>(null);
  const [selectedMemberForActivation, setSelectedMemberForActivation] = useState<any>(null);
  const [selectedMemberForPasswordReset, setSelectedMemberForPasswordReset] = useState<any>(null);
  const [selectedMemberForPermissions, setSelectedMemberForPermissions] = useState<any>(null);
  const [selectedMemberForRole, setSelectedMemberForRole] = useState<any>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const [sentInvitations, setSentInvitations] = useState<WorkgroupRequest[]>([]);
  const [joinRequests, setJoinRequests] = useState<WorkgroupRequest[]>([]);
  const [myInvitations, setMyInvitations] = useState<WorkgroupRequest[]>([]);
  const [isSentInvitationsOpen, setIsSentInvitationsOpen] = useState(false);
  const [isJoinRequestsOpen, setIsJoinRequestsOpen] = useState(false);
  const [isMyInvitationsOpen, setIsMyInvitationsOpen] = useState(false);
  const [isJoinWorkgroupOpen, setIsJoinWorkgroupOpen] = useState(false);
  const [requestActioningId, setRequestActioningId] = useState<number | null>(null);

  const [workgroupSearchTerm, setWorkgroupSearchTerm] = useState('');
  const [workgroupSearchResults, setWorkgroupSearchResults] = useState<WorkgroupSearchResult[]>([]);
  const [searchingWorkgroups, setSearchingWorkgroups] = useState(false);
  const [joiningWorkgroupId, setJoiningWorkgroupId] = useState<number | null>(null);

  // `user.role` is a cosmetic, account-wide job-title field (see Profile.tsx
  // / TEAM_ROLES) -- it can't tell owners of THIS workgroup apart from a
  // VIEWER who happens to be looking at it. What actually governs "can this
  // person manage this workgroup and its other members" is the resolved
  // role-permission grid for the active workgroup (see AuthContext.hasPermission),
  // gated per-action rather than a single blanket owner/not-owner flag so a
  // custom role can grant these independently.
  const { activeWorkgroup } = useWorkgroups();
  const isWorkgroupOwner = activeWorkgroup?.role === 'OWNER';
  const canInvite = useHasPermission('teams', 'invite');
  const canManageMembers = useHasPermission('teams', 'update');
  const canDelete = useHasPermission('teams', 'delete');
  const canAssignRole = useHasPermission('teams', 'assign');
  // Every default role grants `roles:read` -- viewing role definitions is a
  // transparency feature for the whole workgroup, distinct from managing
  // them (create/update/delete, gated separately inside RolesManagement).
  const canViewRoles = useHasPermission('roles', 'read');
  const canViewBilling = useHasPermission('billing', 'read');

  // Community is a single-seat plan (teamMemberLimit: 1) -- adding anyone,
  // by invite or by accepting a join request, is blocked server-side by
  // PackageValidationService.validateTeamMemberInvitation. Mirror that here
  // so the buttons grey out with an explanation instead of erroring on click.
  const { packageUsage } = usePackageLimits();
  const teamMemberLimit = packageUsage?.limits.teamMembers;
  const canAddTeamMember = teamMemberLimit?.canInvite ?? true;
  const teamMemberLimitMessage = teamMemberLimit
    ? `Team member limit reached (${teamMemberLimit.current}/${teamMemberLimit.limit}) -- upgrade your plan to add more people.`
    : undefined;

  // Backs the role picker on both the invite dialog and the join-request
  // accept dialog -- same data source ChangeMemberRole.tsx already uses.
  const { data: roles, loading: rolesLoading } = useRoles();
  const defaultRoleId = roles.find((r) => r.rolecode === 'VIEWER')?.id ?? roles[0]?.id;
  const [acceptingRequest, setAcceptingRequest] = useState<WorkgroupRequest | null>(null);
  const [acceptRoleId, setAcceptRoleId] = useState<string>('');

  const loadWorkgroupRequests = async () => {
    if (!canInvite) {
      // These two endpoints require teams.invite (Owner+Admin) -- don't even
      // call them (and don't let apiClient toast a permission error) when we
      // already know from the resolved permission grid that they'd 403.
      setSentInvitations([]);
      setJoinRequests([]);
      return;
    }
    try {
      const [sentRes, requestsRes] = await Promise.all([
        WorkgroupRequestsAPI.listSentInvitations(),
        WorkgroupRequestsAPI.listRequests(),
      ]);
      setSentInvitations(sentRes.data || []);
      setJoinRequests(requestsRes.data || []);
    } catch {
      setSentInvitations([]);
      setJoinRequests([]);
    }
  };

  const loadMyInvitations = async () => {
    try {
      const res = await WorkgroupRequestsAPI.listInvitations();
      setMyInvitations(res.data || []);
    } catch {
      setMyInvitations([]);
    }
  };

  useEffect(() => {
    loadWorkgroupRequests();
  }, [workgroupId, canInvite]);

  useEffect(() => {
    loadMyInvitations();
  }, []);

  useEffect(() => {
    const term = workgroupSearchTerm.trim();
    if (!term) {
      setWorkgroupSearchResults([]);
      return;
    }
    // A search still in flight when the term changes (or the dialog closes and
    // clears it) must not write its stale results back afterwards.
    let cancelled = false;
    const timeout = setTimeout(async () => {
      setSearchingWorkgroups(true);
      try {
        const res = await WorkgroupRequestsAPI.search(term);
        if (!cancelled) setWorkgroupSearchResults(res.data || []);
      } catch {
        if (!cancelled) setWorkgroupSearchResults([]);
      } finally {
        if (!cancelled) setSearchingWorkgroups(false);
      }
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(timeout);
      setSearchingWorkgroups(false);
    };
  }, [workgroupSearchTerm]);

  const pendingJoinRequestsCount = joinRequests.filter(r => r.requeststatus === 'PENDING').length;
  const pendingSentInvitationsCount = sentInvitations.filter(r => r.requeststatus === 'PENDING').length;

  const requestPersonName = (row: WorkgroupRequest) => {
    const p = row.profiles_profileidToprofiles;
    if (p) return `${p.firstname ?? ''} ${p.lastname ?? ''}`.trim() || p.emailid;
    return row.email || 'Unknown';
  };

  const inviterName = (row: WorkgroupRequest) => {
    const p = row.requestedbyprofile;
    if (!p) return 'Someone';
    return `${p.firstname ?? ''} ${p.lastname ?? ''}`.trim() || p.emailid;
  };

  const handleAcceptJoinRequest = async (id: number, roleId?: number) => {
    setRequestActioningId(id);
    try {
      await WorkgroupRequestsAPI.accept(id, roleId);
      toast.success('Request accepted');
      await Promise.all([loadWorkgroupRequests(), refetch()]);
    } catch {
      // apiClient already surfaced the error as a toast.
    } finally {
      setRequestActioningId(null);
    }
  };

  const handleRejectJoinRequest = async (id: number) => {
    setRequestActioningId(id);
    try {
      await WorkgroupRequestsAPI.reject(id);
      toast.success('Request declined');
      await loadWorkgroupRequests();
    } catch {
    } finally {
      setRequestActioningId(null);
    }
  };

  const handleAcceptMyInvitation = async (id: number) => {
    setRequestActioningId(id);
    try {
      await WorkgroupRequestsAPI.accept(id);
      toast.success('Invitation accepted');
      await loadMyInvitations();
    } catch {
    } finally {
      setRequestActioningId(null);
    }
  };

  const handleDeclineMyInvitation = async (id: number) => {
    setRequestActioningId(id);
    try {
      await WorkgroupRequestsAPI.reject(id);
      toast.success('Invitation declined');
      await loadMyInvitations();
    } catch {
    } finally {
      setRequestActioningId(null);
    }
  };

  // Every way of closing the Join dialog (X, Esc, outside click) starts the
  // next open from a blank search. Pending status isn't lost: the search
  // endpoint reports requestPending per workgroup.
  const closeJoinWorkgroupDialog = () => {
    setIsJoinWorkgroupOpen(false);
    setWorkgroupSearchTerm('');
    setWorkgroupSearchResults([]);
  };

  const handleRequestToJoinWorkgroup = async (targetWorkgroupId: number) => {
    setJoiningWorkgroupId(targetWorkgroupId);
    try {
      await WorkgroupRequestsAPI.join(targetWorkgroupId);
      toast.success('Request sent');
      setWorkgroupSearchResults((prev) =>
        prev.map((r) => (r.id === targetWorkgroupId ? { ...r, requestPending: true, pendingType: 'JOIN_REQUEST' } : r))
      );
    } catch {
    } finally {
      setJoiningWorkgroupId(null);
    }
  };
  const {
    data: allTeamMembers,
    loading,
    error,
    resetMemberPassword,
    deleteMember,
    activateMember,
    deactivateMember,
    updateMemberRole,
    updateMemberPermissions,
    refetch
  } = useTeams({
    workgroupId: workgroupId,
  });

  const getSortDisplayTitle = (sortKey: string) => {
    const sortTitles: Record<string, string> = {
      'firstname': 'First Name',
      'lastname': 'Last Name',
      'emailid': 'Email',
      'role': 'Role',
      'department': 'Department',
      'designation': 'Designation',
      'createdon': 'Joined Date',
      'status': 'Status'
    };
    return sortTitles[sortKey] || sortKey;
  };

  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRoleId, setInviteRoleId] = useState<string>('');

  useEffect(() => {
    if (isInviteDialogOpen && !inviteRoleId && defaultRoleId) {
      setInviteRoleId(String(defaultRoleId));
    }
  }, [isInviteDialogOpen, defaultRoleId]);

  const handleInviteSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    setIsSubmitting(true);
    try {
      const response = await WorkgroupRequestsAPI.invite(inviteEmail.trim(), inviteRoleId ? Number(inviteRoleId) : undefined);
      toast.success(response.data?.message || 'Invitation sent!');
      setIsInviteDialogOpen(false);
      setInviteEmail('');
      setInviteRoleId('');
      loadWorkgroupRequests();
    } catch {
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteMember = (member: any) => {
    if (member.id === user?.id) {
      toast.error('You cannot delete your own account');
      return;
    }
    setSelectedMemberForDelete(member);
    setDeleteModalOpen(true);
  };

  const handleDeleteSuccess = () => {
    setSelectedMemberForDelete(null);
    setDeleteModalOpen(false);
  };

  const roleLabel = (role?: string | null) => {
    switch (role) {
      case 'OWNER': return 'Owner';
      case 'ADMIN': return 'Admin';
      case 'MEMBER': return 'Member';
      case 'VIEWER': return 'Viewer';
      default: return role || 'Viewer';
    }
  };

  const {
    filteredTeamMembers,
    paginatedTeamMembers,
  } = useMemo(() => {
    const filtered = allTeamMembers?.filter(member => {
      const searchMatch = searchTerm === '' ||
        member.firstname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        member.lastname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        member.emailid?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        member.role?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        member.department?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        member.designation?.toLowerCase().includes(searchTerm.toLowerCase());

    
      const departmentMatch = departmentFilter === 'all' ||
        member.department?.toLowerCase() === departmentFilter.toLowerCase();

 
      const designationMatch = designationFilter === 'all' ||
        member.designation?.toLowerCase() === designationFilter.toLowerCase();

      const statusMatch = statusFilter === 'all' || member.status === statusFilter;

      return searchMatch && departmentMatch && designationMatch && statusMatch;
    }) || [];

    const sorted = [...filtered].sort((a, b) => {
      // The workgroup owner always sits at the top of the list, ahead of
      // whatever other sort is applied.
      if (a.workgrouprole === 'OWNER' && b.workgrouprole !== 'OWNER') return -1;
      if (b.workgrouprole === 'OWNER' && a.workgrouprole !== 'OWNER') return 1;

      let aValue: any, bValue: any;

      switch (sortBy) {
        case 'firstname':
          aValue = a.firstname || '';
          bValue = b.firstname || '';
          break;
        case 'lastname':
          aValue = a.lastname || '';
          bValue = b.lastname || '';
          break;
        case 'emailid':
          aValue = a.emailid || '';
          bValue = b.emailid || '';
          break;
       
        case 'department':
          aValue = a.department || '';
          bValue = b.department || '';
          break;
        case 'designation':
          aValue = a.designation || '';
          bValue = b.designation || '';
          break;
        case 'createdon':
          aValue = a.registeredon || '';
          bValue = b.registeredon || '';
          break;
        case 'status':
          aValue = a.status || '';
          bValue = b.status || '';
          break;
        default:
          aValue = a.firstname || '';
          bValue = b.firstname || '';
      }

      if (sortBy === 'createdon') {
        const dateA = new Date(aValue);
        const dateB = new Date(bValue);
        if (sortOrder === 'asc') {
          return dateA > dateB ? 1 : -1;
        } else {
          return dateA < dateB ? 1 : -1;
        }
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    const itemsPerPage = 9;
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginated = sorted.slice(startIndex, endIndex);

    return {
      filteredTeamMembers: filtered,
      paginatedTeamMembers: paginated,
    };
  }, [allTeamMembers, searchTerm, departmentFilter, designationFilter, statusFilter, currentPage, sortBy, sortOrder]);

  const hasActiveFilters = statusFilter !== 'all' ||
    departmentFilter !== 'all' ||
    designationFilter !== 'all' ||
    searchTerm.length > 0;

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleStatusFilter = (status: string) => {
    setStatusFilter(status);
    setCurrentPage(1);
  };

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
    setCurrentPage(1);
  };

  const handleDepartmentFilter = (department: string) => {
    setDepartmentFilter(department);
    setCurrentPage(1);
  };

  const handleDesignationFilter = (designation: string) => {
    setDesignationFilter(designation);
    setCurrentPage(1);
  };

  const clearAllFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setDepartmentFilter('all');
    setDesignationFilter('all');
    setCurrentPage(1);
  };

  const TeamMemberCard = ({ member }: { member: any }) => {
    const actions = [
      {
        label: 'View Details',
        icon: <Eye className="w-4 h-4 mr-2" />,
        onClick: () => setSelectedMemberForView(member),
        show: true,
        variant: 'default'
      },
      {
        label: member.account_status === 'Active' ? 'Deactivate' : 'Activate',
        icon: member.account_status === 'Active' ? <UserX className="w-4 h-4 mr-2" /> : <UserCheck className="w-4 h-4 mr-2" />,
        onClick: () => setSelectedMemberForActivation(member),
        show: canManageMembers && member.id !== user?.id,
        variant: 'default'
      },
      // Disabled: Reset Password is non-functional on the backend (generates a
      // temp password but never persists or emails it) and misleads admins with
      // a false "email sent" message. Hidden from the menu until fixed.
      // {
      //   label: 'Reset Password',
      //   icon: <Key className="w-4 h-4 mr-2" />,
      //   onClick: () => setSelectedMemberForPasswordReset(member),
      //   show: canManageMembers && member.account_status === 'Active',
      //   variant: 'default'
      // },
      {
        label: 'Change Role',
        icon: <Shield className="w-4 h-4 mr-2" />,
        onClick: () => setSelectedMemberForRole(member),
        show: canAssignRole && member.id !== user?.id && member.workgrouprole !== 'OWNER' && member.account_status === 'Active',
        variant: 'default'
      },
      {
        label: 'Manage Permissions',
        icon: <Shield className="w-4 h-4 mr-2" />,
        onClick: () => setSelectedMemberForPermissions(member),
        show: canManageMembers && member.id !== user?.id && member.account_status === 'Active',
        variant: 'default'
      },
      {
        label: 'Delete Member',
        icon: <Trash2 className="w-4 h-4 mr-2" />,
        onClick: () => handleDeleteMember(member),
        show: canDelete && member.id !== user?.id,
        variant: 'danger'
      }
    ];

    return (
      <Card key={member.id} className="bg-card border-border shadow-sm hover:shadow-md transition-shadow gap-0">
          <CardContent className="p-4">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 min-w-0">
                <Avatar className="w-10 h-10 shrink-0">
                  <AvatarImage src={member.avatar} alt={`${member.firstname}`} />
                  <AvatarFallback className="bg-primary text-primary-foreground text-sm">
                    {(member.firstname?.[0] || '')}{(member.lastname?.[0] || '')}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0">
                  <h3 className="font-semibold text-foreground truncate">
                    {member.firstname} {member.lastname}{member.id === user?.id ? ' (you)' : ''}
                  </h3>
                  <p className="text-sm text-muted-foreground truncate">
                    {member.emailid}
                    {member.registeredon ? ` · joined ${formatMemberSinceDate(member.registeredon)}` : ''}
                    {member.total_scans_created !== undefined ? ` · ${member.total_scans_created} scans` : ''}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                <Badge variant="outline" className="text-xs">{member.workgrouprolename || roleLabel(member.workgrouprole)}</Badge>
                <Badge className={getStatusColor(member.status)}>
                  {member.status}
                </Badge>
                {canDelete && member.id !== user?.id && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-8 h-8 p-0 text-muted-foreground hover:text-red-600"
                    onClick={() => handleDeleteMember(member)}
                    title="Delete Member"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="w-8 p-0">
                      <MoreHorizontal className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-popover shadow-lg rounded-lg border border-border">
                    {actions.filter(action => action.show && action.label !== 'Delete Member').map((action, index) => (
                      <DropdownMenuItem
                        key={index}
                        onClick={action.onClick}
                        className={action.variant === 'danger' ? 'text-red-600' : ''}
                      >
                        {action.icon}
                        {action.label}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-center py-12">
          <div className="text-muted-foreground">Loading team members...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4 max-w-full" style={{ paddingTop: "10px" }}>
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-lg bg-primary/10 text-primary shrink-0">
          <Building2 className="w-5 h-5" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-foreground">Workspaces &amp; Memberships</h2>
          <p className="text-sm text-muted-foreground">Where you belong, what you can do there, and who is waiting for your approval.</p>
        </div>
      </div>

      <Tabs defaultValue="membership">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <TabsList>
            <TabsTrigger value="membership">Workspaces & Memberships</TabsTrigger>
            {canViewRoles && <TabsTrigger value="roles">Roles & Permissions</TabsTrigger>}
            {canViewBilling && <TabsTrigger value="billing">Billing</TabsTrigger>}
          </TabsList>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              className="bg-white rounded-lg shadow-sm border-border"
              onClick={() => setIsMyInvitationsOpen(true)}
            >
              <Mail className="w-4 h-4 mr-2" />
              My Invitations
              {myInvitations.length > 0 && (
                <Badge variant="secondary" className="ml-2">{myInvitations.length}</Badge>
              )}
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="bg-white rounded-lg shadow-sm border-border"
              onClick={() => setIsJoinWorkgroupOpen(true)}
            >
              <SearchIcon className="w-4 h-4 mr-2" />
              Join a Workgroup
            </Button>
          </div>
        </div>

        <TabsContent value="membership" className="space-y-2">
          <Card className="bg-card border-border shadow-sm">
            <CardContent className="p-4 space-y-3">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="font-semibold text-base text-foreground">{activeWorkgroup?.title}</div>
                  <div className="text-sm text-muted-foreground">
                    {canInvite
                      ? 'You can approve join requests and change member roles in this workgroup.'
                      : "You can view this workgroup's members."}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {subscription?.package?.title && (
                    <Badge variant="outline">{subscription.package.title}</Badge>
                  )}
                  {activeWorkgroup?.role && (
                    <Badge>{roleLabel(activeWorkgroup.role)}</Badge>
                  )}
                  {isSubscriptionValid(subscription?.validupto || null) && canInvite && (
                    <GatedControl
                      allowed={canAddTeamMember}
                      message={teamMemberLimitMessage}
                      fallback={
                        <Button size="sm" className="h-7 px-3 bg-[#ececf0] text-[#717182] hover:bg-primary hover:text-primary-foreground rounded-lg shadow-sm text-xs whitespace-nowrap">
                          <Plus className="w-3.5 h-3.5 mr-1" />
                          <span>Invite Member</span>
                        </Button>
                      }
                    >
                    <Dialog open={isInviteDialogOpen} onOpenChange={setIsInviteDialogOpen}>
                      <DialogTrigger asChild>
                        <Button size="sm" className="h-7 px-3 bg-[#ececf0] text-[#717182] hover:bg-primary hover:text-primary-foreground rounded-lg shadow-sm text-xs whitespace-nowrap">
                          <Plus className="w-3.5 h-3.5 mr-1" />
                          <span>Invite Member</span>
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="sm:max-w-[425px] bg-popover">
                        <DialogHeader>
                          <DialogTitle>Invite to Your Workgroup</DialogTitle>
                          <DialogDescription>
                            Enter their email. If they already have an account, they'll get an invite to accept from their Invitations page. Otherwise we'll email them to sign up first.
                          </DialogDescription>
                        </DialogHeader>
                        <form onSubmit={handleInviteSubmit}>
                          <div className="grid gap-4 py-4">
                            <div className="grid grid-cols-4 items-center gap-4">
                              <Label htmlFor="emailid" className="text-right">
                                Email
                              </Label>
                              <Input
                                id="emailid"
                                type="email"
                                value={inviteEmail}
                                onChange={(e) => setInviteEmail(e.target.value)}
                                className="col-span-3"
                                placeholder="Enter email address"
                                maxLength={320}
                                required
                              />
                            </div>
                            <div className="grid grid-cols-4 items-center gap-4">
                              <Label htmlFor="inviteRole" className="text-right">
                                Role
                              </Label>
                              <Select value={inviteRoleId} onValueChange={setInviteRoleId} disabled={rolesLoading}>
                                <SelectTrigger id="inviteRole" className="col-span-3">
                                  <SelectValue placeholder={rolesLoading ? 'Loading roles...' : 'Select a role'} />
                                </SelectTrigger>
                                <SelectContent>
                                  {roles.map((role) => (
                                    <SelectItem key={role.id} value={String(role.id)}>
                                      {role.rolename}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          <DialogFooter>
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => setIsInviteDialogOpen(false)}
                              disabled={isSubmitting}
                            >
                              Cancel
                            </Button>
                            <Button type="submit" disabled={isSubmitting}>
                              {isSubmitting ? 'Sending...' : 'Send Invitation'}
                            </Button>
                          </DialogFooter>
                        </form>
                      </DialogContent>
                    </Dialog>
                    </GatedControl>
                  )}
                </div>
              </div>
              <Tabs defaultValue="members">
                <TabsList>
                  <TabsTrigger value="members">
                    <Users className="w-4 h-4 mr-2" />
                    View Members
                    <Badge variant="secondary" className="ml-2">{allTeamMembers?.length || 0}</Badge>
                  </TabsTrigger>
                  <GatedControl allowed={canAddTeamMember} message={teamMemberLimitMessage}>
                    <TabsTrigger value="sent">
                      <Send className="w-4 h-4 mr-2" />
                      Invitations Sent
                      {sentInvitations.length > 0 && (
                        <Badge variant="secondary" className="ml-2">{pendingSentInvitationsCount || sentInvitations.length}</Badge>
                      )}
                    </TabsTrigger>
                  </GatedControl>
                  <TabsTrigger value="requests">
                    <UserPlus className="w-4 h-4 mr-2" />
                    Join Requests
                    {pendingJoinRequestsCount > 0 && (
                      <Badge variant="secondary" className="ml-2">{pendingJoinRequestsCount}</Badge>
                    )}
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="members" className="pt-3 space-y-2">
                  {error && (
                    <div className="bg-destructive/10 border border-destructive rounded-lg p-4 mb-2">
                      <div className="text-destructive-foreground">{error}</div>
                      <button
                        onClick={refetch}
                        className="text-destructive underline text-sm mt-1"
                      >
                        Try again
                      </button>
                    </div>
                  )}
                  <div className="space-y-3">
                    {filteredTeamMembers.length === 0 ? (
                      <div className="text-center py-12 col-span-full">
                        <div className="text-muted-foreground">
                          {hasActiveFilters ? 'No team members match your filters' : 'No team members found'}
                        </div>
                        {hasActiveFilters && (
                          <Button
                            variant="outline"
                            onClick={clearAllFilters}
                            className="mt-2"
                          >
                            Clear Filters
                          </Button>
                        )}
                      </div>
                    ) : (
                      filteredTeamMembers.map((member) => (
                        <TeamMemberCard key={member.id} member={member} />
                      ))
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="sent" className="pt-3 space-y-2">
                  {sentInvitations.length === 0 ? (
                    <div className="text-sm text-muted-foreground text-center py-6">No invitations sent yet.</div>
                  ) : (
                    sentInvitations.map((inv) => (
                      <div key={inv.id} className="flex items-center justify-between p-3 rounded-lg border border-border">
                        <div>
                          <div className="font-medium text-sm">{requestPersonName(inv)}</div>
                          <div className="text-xs text-muted-foreground">
                            Sent {new Date(inv.requestedon).toLocaleDateString()}
                          </div>
                        </div>
                        <Badge
                          className={
                            inv.requeststatus === 'ACCEPTED'
                              ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                              : inv.requeststatus === 'REJECTED'
                                ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                                : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                          }
                        >
                          {inv.requeststatus}
                        </Badge>
                      </div>
                    ))
                  )}
                </TabsContent>

                <TabsContent value="requests" className="pt-3 space-y-2">
                  {joinRequests.filter(r => r.requeststatus === 'PENDING').length === 0 ? (
                    <div className="text-sm text-muted-foreground text-center py-6">No pending join requests.</div>
                  ) : (
                    joinRequests.filter(r => r.requeststatus === 'PENDING').map((req) => (
                      <div key={req.id} className="flex items-center justify-between p-3 rounded-lg border border-border">
                        <div>
                          <div className="font-medium text-sm">{requestPersonName(req)}</div>
                          <div className="text-xs text-muted-foreground">
                            {req.profiles_profileidToprofiles?.emailid}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={requestActioningId === req.id}
                            onClick={() => handleRejectJoinRequest(req.id)}
                          >
                            <X className="w-4 h-4 mr-1" /> Decline
                          </Button>
                          <GatedControl allowed={canAddTeamMember} message={teamMemberLimitMessage}>
                            <Button
                              size="sm"
                              disabled={requestActioningId === req.id}
                              onClick={() => {
                                setAcceptingRequest(req);
                                setAcceptRoleId(defaultRoleId ? String(defaultRoleId) : '');
                              }}
                            >
                              <Check className="w-4 h-4 mr-1" /> Accept
                            </Button>
                          </GatedControl>
                        </div>
                      </div>
                    ))
                  )}
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>

      <Dialog open={!!acceptingRequest} onOpenChange={(open) => !open && setAcceptingRequest(null)}>
        <DialogContent className="sm:max-w-[425px] bg-popover">
          <DialogHeader>
            <DialogTitle>Accept Join Request</DialogTitle>
            <DialogDescription>
              Choose a role for {acceptingRequest && requestPersonName(acceptingRequest)} before adding them to the workgroup.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Select value={acceptRoleId} onValueChange={setAcceptRoleId} disabled={rolesLoading}>
              <SelectTrigger>
                <SelectValue placeholder={rolesLoading ? 'Loading roles...' : 'Select a role'} />
              </SelectTrigger>
              <SelectContent>
                {roles.map((role) => (
                  <SelectItem key={role.id} value={String(role.id)}>
                    {role.rolename}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setAcceptingRequest(null)}
              disabled={!!acceptingRequest && requestActioningId === acceptingRequest.id}
            >
              Cancel
            </Button>
            <Button
              disabled={!acceptRoleId || (!!acceptingRequest && requestActioningId === acceptingRequest.id)}
              onClick={async () => {
                if (!acceptingRequest) return;
                await handleAcceptJoinRequest(acceptingRequest.id, Number(acceptRoleId));
                setAcceptingRequest(null);
              }}
            >
              {acceptingRequest && requestActioningId === acceptingRequest.id ? 'Accepting...' : 'Accept'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="text-xs text-muted-foreground pt-3 border-t border-border">
        <span className="font-medium text-foreground">Owner</span> (full control) · <span className="font-medium text-foreground">Admin</span> (manage members and scans) · <span className="font-medium text-foreground">Member</span> (run scans) · <span className="font-medium text-foreground">Viewer</span> (read only)
      </div>

      <ActivateDeactivateTeamMember
        isOpen={selectedMemberForActivation !== null}
        onClose={() => setSelectedMemberForActivation(null)}
        member={selectedMemberForActivation}
        onSuccess={refetch}
        activateMember={activateMember}
        deactivateMember={deactivateMember}
      />

      <ResetMemberPassword
        isOpen={selectedMemberForPasswordReset !== null}
        onClose={() => setSelectedMemberForPasswordReset(null)}
        member={selectedMemberForPasswordReset}
        onSuccess={refetch}
        resetMemberPassword={resetMemberPassword}
      />

      <EditPermissions
        isOpen={selectedMemberForPermissions !== null}
        onClose={() => setSelectedMemberForPermissions(null)}
        member={selectedMemberForPermissions}

        updateMemberPermissions={updateMemberPermissions}
      />

      <ChangeMemberRole
        isOpen={selectedMemberForRole !== null}
        onClose={() => setSelectedMemberForRole(null)}
        member={selectedMemberForRole}
        onSuccess={refetch}
        updateMemberRole={updateMemberRole}
      />

      <DeleteTeamMember
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        teammember={selectedMemberForDelete}
        onSuccess={handleDeleteSuccess}
        deleteMember={deleteMember}
      />

      <TeamMemberDetails
        isOpen={selectedMemberForView !== null}
        onClose={() => setSelectedMemberForView(null)}
        member={selectedMemberForView}
      />
        </TabsContent>

        {canViewRoles && (
          <TabsContent value="roles">
            <RolesManagement />
          </TabsContent>
        )}

        {canViewBilling && (
          <TabsContent value="billing">
            <Billing />
          </TabsContent>
        )}
      </Tabs>

      <Dialog open={isMyInvitationsOpen} onOpenChange={setIsMyInvitationsOpen}>
        <DialogContent className="sm:max-w-[500px] bg-popover">
          <DialogHeader>
            <DialogTitle>My Invitations</DialogTitle>
            <DialogDescription>
              Workgroups that have invited you.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto space-y-2">
            {myInvitations.length === 0 ? (
              <div className="text-sm text-muted-foreground text-center py-6">No pending invitations.</div>
            ) : (
              myInvitations.map((inv) => (
                <div key={inv.id} className="flex items-center justify-between p-3 rounded-lg border border-border">
                  <div>
                    <div className="font-medium text-sm">{inv.workgroups?.title}</div>
                    <div className="text-xs text-muted-foreground">
                      Invited by {inviterName(inv)}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={requestActioningId === inv.id}
                      onClick={() => handleDeclineMyInvitation(inv.id)}
                    >
                      <X className="w-4 h-4 mr-1" /> Decline
                    </Button>
                    <Button
                      size="sm"
                      disabled={requestActioningId === inv.id}
                      onClick={() => handleAcceptMyInvitation(inv.id)}
                    >
                      <Check className="w-4 h-4 mr-1" /> Accept
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={isJoinWorkgroupOpen}
        onOpenChange={(open) => (open ? setIsJoinWorkgroupOpen(true) : closeJoinWorkgroupDialog())}
      >
        <DialogContent className="sm:max-w-[500px] bg-popover">
          <DialogHeader>
            <DialogTitle>Join a Workgroup</DialogTitle>
            <DialogDescription>
              Search by workgroup name or id, then request to join.
            </DialogDescription>
          </DialogHeader>
          <Input
            placeholder="Search workgroups..."
            value={workgroupSearchTerm}
            onChange={(e) => setWorkgroupSearchTerm(e.target.value)}
            maxLength={100}
          />
          <div className="max-h-[50vh] overflow-y-auto space-y-2">
            {searchingWorkgroups && <div className="text-sm text-muted-foreground text-center py-4">Searching...</div>}
            {!searchingWorkgroups && workgroupSearchTerm.trim() && workgroupSearchResults.length === 0 && (
              <div className="text-sm text-muted-foreground text-center py-4">No workgroups found.</div>
            )}
            {workgroupSearchResults.map((wg) => (
              <div key={wg.id} className="flex items-center justify-between p-3 rounded-lg border border-border">
                <div>
                  <div className="font-medium text-sm">{wg.title}</div>
                  <div className="text-xs text-muted-foreground">#{wg.id}</div>
                </div>
                <Button
                  size="sm"
                  disabled={wg.requestPending || joiningWorkgroupId === wg.id}
                  onClick={() => handleRequestToJoinWorkgroup(wg.id)}
                >
                  {wg.pendingType === 'INVITATION'
                    ? 'Invitation Pending'
                    : wg.requestPending
                      ? 'Request Pending'
                      : joiningWorkgroupId === wg.id
                        ? 'Sending...'
                        : 'Request to Join'}
                </Button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}